import { useState, useEffect, type FormEvent } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/forms/PageHeader";

export function ConfigPage() {
  const { user, isAdmin } = useAuth();
  const [config, setConfig] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase
      .from("configuracion")
      .select("clave, valor")
      .then(({ data }) => {
        if (data) {
          const obj: Record<string, string> = {};
          data.forEach((row) => { obj[row.clave] = row.valor; });
          setConfig(obj);
        }
      });
  }, []);

  async function guardar(clave: string, valor: string) {
    setSaving(true);
    const { error } = await supabase.from("configuracion").update({ valor, modificado_por: user?.id }).eq("clave", clave);
    setSaving(false);
    if (error) { toast.error("Error", { description: error.message }); return; }
    toast.success("Guardado");
    setConfig((c) => ({ ...c, [clave]: valor }));
  }

  return (
    <div className="max-w-2xl space-y-4">
      <PageHeader title="Configuración" />
      <section className="card p-4 space-y-3">
        <h2 className="font-semibold">Mi cuenta</h2>
        <p className="text-sm text-fg-muted">{user?.nombre_completo} · {user?.email}</p>
        <p className="text-sm text-fg-muted">Rol: {user?.rol}</p>
      </section>
      <CambiarPasswordSection />
      {isAdmin && (
        <section className="card p-4 space-y-3">
          <h2 className="font-semibold">Sistema</h2>
          <ConfigField label="Email del contador" value={config.email_contador ?? ""} type="email" onSave={(v) => guardar("email_contador", v)} saving={saving} hint="Recibe reportes mensuales" />
          <ConfigField label="Días para alertar liquidación" value={config.dias_alerta_liquidar ?? "3"} type="number" onSave={(v) => guardar("dias_alerta_liquidar", v)} saving={saving} />
          <ConfigField label="Días para alertar pago" value={config.dias_alerta_pagar ?? "2"} type="number" onSave={(v) => guardar("dias_alerta_pagar", v)} saving={saving} />
          <ConfigField label="Días para alertar devolución pendiente" value={config.dias_alerta_devolucion ?? "7"} type="number" onSave={(v) => guardar("dias_alerta_devolucion", v)} saving={saving} />
          <ConfigField label="Duración corte (días)" value={config.duracion_corte_dias ?? "15"} type="number" onSave={(v) => guardar("duracion_corte_dias", v)} saving={saving} />
        </section>
      )}
    </div>
  );
}

function CambiarPasswordSection() {
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = pass1.length > 0 && pass1.length < 8;
  const noMatch = pass2.length > 0 && pass1 !== pass2;
  const valid = pass1.length >= 8 && pass1 === pass2;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: pass1 });
    setSubmitting(false);
    if (error) { toast.error("No se pudo cambiar la contraseña", { description: error.message }); return; }
    toast.success("Contraseña actualizada", { description: "Usa la nueva contraseña la próxima vez que entres." });
    setPass1("");
    setPass2("");
  }

  return (
    <section className="card p-4 space-y-3">
      <h2 className="font-semibold">Cambiar contraseña</h2>
      <p className="text-sm text-fg-muted">Mínimo 8 caracteres. Recomendado: mezcla mayúsculas, números y símbolos.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">Nueva contraseña</label>
          <input type={show ? "text" : "password"} value={pass1} onChange={(e) => setPass1(e.target.value)} className="input" autoComplete="new-password" placeholder="Mínimo 8 caracteres" />
          {tooShort && (<p className="text-xs text-warning">Faltan {8 - pass1.length} caracteres</p>)}
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Confirmar contraseña</label>
          <input type={show ? "text" : "password"} value={pass2} onChange={(e) => setPass2(e.target.value)} className="input" autoComplete="new-password" placeholder="Repite la misma contraseña" />
          {noMatch && (<p className="text-xs text-error">Las contraseñas no coinciden</p>)}
          {valid && <p className="text-xs text-success">✓ Coinciden</p>}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="size-4" />
          Mostrar contraseña
        </label>
        <button type="submit" disabled={!valid || submitting} className="btn-primary">
          {submitting ? "Cambiando..." : "Cambiar contraseña"}
        </button>
      </form>
    </section>
  );
}

function ConfigField({ label, value, type = "text", hint, saving, onSave }: { label: string; value: string; type?: string; hint?: string; saving: boolean; onSave: (v: string) => void; }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const dirty = v !== value;

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <div className="flex gap-2">
        <input type={type} value={v} onChange={(e) => setV(e.target.value)} className="input flex-1" />
        {dirty && (<button onClick={() => onSave(v)} disabled={saving} className="btn-primary text-sm">Guardar</button>)}
      </div>
      {hint && <p className="text-xs text-fg-muted">{hint}</p>}
    </div>
  );
}
